import { useEffect, useState } from "react";
import { Link, Routes, Route, useNavigate, Navigate, Outlet, useLocation } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import {
  LogOut, Save, Trash2, Plus, Download, RotateCcw, ArrowLeft,
  Globe, Settings2, Gift, ArrowRight, Sparkles,
} from "lucide-react";
import MediaPanel from "@/pages/MediaPanel";
import GuideEditor from "@/pages/GuideEditor";
import GiftCardsPanel from "@/pages/GiftCardsPanel";
import HomePanel from "@/pages/HomePanel";
import GestionLayout from "@/pages/gestion/GestionLayout";
import GestionToday from "@/pages/gestion/Today";
import GestionFinances from "@/pages/gestion/Finances";
import GestionCalendar from "@/pages/gestion/Calendar";
import GestionDashboard from "@/pages/gestion/Dashboard";
import GestionGuests from "@/pages/gestion/GuestsList";
import GestionGuestDetail from "@/pages/gestion/GuestDetail";
import GestionSettings from "@/pages/gestion/Settings";
import PrepMasterListPrint from "@/pages/gestion/PrepMasterListPrint";

/**
 * Admin — hub d'administration unifié pour le projet Curieux Hub.
 *
 * Structure des routes :
 *   /admin              → Hub (3 cartes : Site web · Gestion · Bons cadeaux)
 *   /admin/site         → Outils du site (Guide, Textes, Tarifs, Avis, Contact, Médias, Historique)
 *   /admin/bons-cadeaux → Gestion des bons cadeaux
 *   /admin/gestion      → Application interne Refuge Guests (placeholder tant que le code
 *                          de l'ancien projet n'a pas été rapatrié)
 *
 * L'authentification (JWT dans cookie httpOnly) est mutualisée sur toutes ces routes.
 * Une seule connexion suffit à accéder à l'ensemble.
 */

function formatError(detail) {
  if (!detail) return "Erreur";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(" ");
  return String(detail);
}

// ============================================================
// LOGIN
// ============================================================
function Login() {
  const [email, setEmail] = useState("");
  const [pw, setPw] = useState("");
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);
  const { login } = useAuth();
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try { await login(email, pw); nav("/admin"); }
    catch (e2) { setErr(formatError(e2.response?.data?.detail) || e2.message); }
    finally { setBusy(false); }
  };
  return (
    <div className="min-h-screen flex items-center justify-center bg-refuge-bg" data-testid="admin-login">
      <form onSubmit={submit} className="max-w-md w-full bg-white p-10 rounded-2xl border border-refuge-border">
        <p className="font-body text-xs tracking-[0.2em] uppercase text-refuge-ruby mb-3">Administration</p>
        <h1 className="font-heading text-3xl font-light text-refuge-text mb-8">Curieux <span className="italic">Hub</span></h1>
        <label className="block font-body text-xs uppercase tracking-wider text-refuge-soft mb-2">Email</label>
        <input data-testid="login-email" type="email" value={email} onChange={e=>setEmail(e.target.value)} required className="w-full mb-6 py-3 px-4 border border-refuge-border rounded-lg font-body text-sm" />
        <label className="block font-body text-xs uppercase tracking-wider text-refuge-soft mb-2">Mot de passe</label>
        <input data-testid="login-password" type="password" value={pw} onChange={e=>setPw(e.target.value)} required className="w-full mb-6 py-3 px-4 border border-refuge-border rounded-lg font-body text-sm" />
        {err && <p className="text-refuge-ruby text-sm mb-4">{err}</p>}
        <button data-testid="login-submit" type="submit" disabled={busy} className="w-full py-3 rounded-full bg-refuge-ruby text-white font-body text-sm hover:bg-refuge-ruby/90 disabled:opacity-60">
          {busy ? "Connexion…" : "Se connecter"}
        </button>
        <Link to="/accueil" className="block mt-6 text-center font-body text-xs uppercase tracking-wider text-refuge-soft hover:text-refuge-ruby">← Retour au site</Link>
      </form>
    </div>
  );
}

// ============================================================
// AUTH GUARD — vérifie une seule fois, puis rend les routes
// ============================================================
function AuthGate() {
  const { user, loading } = useAuth();
  if (loading) return <div className="min-h-screen flex items-center justify-center bg-refuge-bg font-heading italic text-refuge-soft">Chargement…</div>;
  if (!user) return <Login />;
  return <Outlet />;
}

// ============================================================
// HEADER partagé
// ============================================================
function AdminHeader({ withBack }) {
  const { user, logout } = useAuth();
  return (
    <header className="bg-white border-b border-refuge-border px-4 md:px-6 py-3 md:py-4 flex items-center justify-between">
      <div className="flex items-center gap-3 md:gap-6 min-w-0">
        {withBack && (
          <Link
            to="/admin"
            data-testid="admin-back-hub"
            className="inline-flex items-center gap-1.5 text-refuge-soft hover:text-refuge-ruby font-body text-xs uppercase tracking-[0.16em] shrink-0"
          >
            <ArrowLeft size={13} strokeWidth={1.5} />
            <span className="hidden sm:inline">Admin</span>
          </Link>
        )}
        <Link to="/accueil" className="font-heading text-lg md:text-xl font-light text-refuge-ruby truncate">
          Curieux <span className="italic">Refuge</span>
        </Link>
        <span className="hidden md:inline font-body text-xs tracking-[0.2em] uppercase text-refuge-soft">Admin</span>
      </div>
      <div className="flex items-center gap-2 md:gap-3 shrink-0">
        <span className="hidden md:inline font-body text-xs text-refuge-soft truncate max-w-[180px]">{user?.email}</span>
        <button
          onClick={logout}
          data-testid="logout-btn"
          className="inline-flex items-center gap-1 text-refuge-soft hover:text-refuge-ruby p-1.5"
          aria-label="Se déconnecter"
        >
          <LogOut size={16} strokeWidth={1.4} />
        </button>
      </div>
    </header>
  );
}

// ============================================================
// HUB — page d'entrée /admin avec 3 accès principaux
// ============================================================
function AdminHub() {
  return (
    <div className="min-h-screen bg-refuge-bg" data-testid="admin-hub">
      <AdminHeader />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10 md:py-16">
        <div className="text-center mb-10 md:mb-14">
          <p className="font-body text-[10px] tracking-[0.36em] uppercase text-refuge-ruby mb-3">
            Administration
          </p>
          <h1 className="font-heading text-3xl md:text-4xl font-light text-refuge-text leading-tight">
            Bienvenue au <span className="italic">tableau de bord</span>
          </h1>
          <p className="font-body italic text-refuge-soft mt-3 text-sm">
            Choisissez l'espace que vous souhaitez ouvrir.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          <HubCard
            to="/admin/site"
            testid="hub-site"
            icon={<Globe size={22} strokeWidth={1.3} />}
            eyebrow="Espace 1"
            title="Site web"
            desc="Textes, tarifs, avis, contact, médias, Guide et historique du site officiel."
            cta="Gérer le site"
          />
          <HubCard
            to="/admin/gestion"
            testid="hub-gestion"
            icon={<Settings2 size={22} strokeWidth={1.3} />}
            eyebrow="Espace 2"
            title="Application de gestion"
            desc="Calendrier, réservations, fiches voyageurs, ménage, marché, checklists."
            cta="Ouvrir la gestion du refuge"
            highlight
          />
          <HubCard
            to="/admin/bons-cadeaux"
            testid="hub-bons"
            icon={<Gift size={22} strokeWidth={1.3} />}
            eyebrow="Espace 3"
            title="Bons cadeaux"
            desc="Demandes reçues, statuts, code cadeau, expérience digitale et carte imprimable."
            cta="Ouvrir les bons cadeaux"
          />
        </div>
      </main>
    </div>
  );
}

function HubCard({ to, testid, icon, eyebrow, title, desc, cta, highlight }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="group bg-white rounded-2xl border border-refuge-border p-5 md:p-6 hover:border-refuge-ruby transition-all block"
      style={
        highlight
          ? { boxShadow: "0 10px 30px -18px rgba(139, 37, 48, 0.35)" }
          : { boxShadow: "0 4px 18px -14px rgba(46, 31, 24, 0.20)" }
      }
    >
      <div
        className="w-11 h-11 rounded-full flex items-center justify-center mb-4"
        style={{
          background: highlight ? "var(--cr-ruby)" : "rgba(232, 184, 189, 0.18)",
          color: highlight ? "#FAF4EC" : "var(--cr-ruby)",
        }}
      >
        {icon}
      </div>
      <p className="font-body text-[10px] tracking-[0.28em] uppercase text-refuge-ruby mb-2">
        {eyebrow}
      </p>
      <p className="font-heading text-xl md:text-2xl font-light text-refuge-text mb-2 leading-tight">
        {title}
      </p>
      <p className="font-body text-sm text-refuge-soft leading-relaxed mb-5">
        {desc}
      </p>
      <span className="inline-flex items-center gap-1.5 font-body text-[11px] tracking-[0.2em] uppercase text-refuge-ruby group-hover:gap-2.5 transition-all">
        {cta}
        <ArrowRight size={13} strokeWidth={1.4} />
      </span>
    </Link>
  );
}

// ============================================================
// PANELS internes (Reviews, History, JsonEditor) — inchangés
// ============================================================
function JsonEditor({ label, value, onSave, testid }) {
  const [txt, setTxt] = useState(JSON.stringify(value ?? {}, null, 2));
  const [msg, setMsg] = useState("");
  useEffect(() => { setTxt(JSON.stringify(value ?? {}, null, 2)); }, [value]);
  const save = async () => {
    try {
      const parsed = JSON.parse(txt);
      await onSave(parsed);
      setMsg("✓ Enregistré"); setTimeout(() => setMsg(""), 2000);
    } catch (e) { setMsg("Erreur : " + (e.message || "JSON invalide")); }
  };
  return (
    <div>
      <div className="flex justify-between items-center mb-3">
        <p className="font-heading text-lg font-light text-refuge-text">{label}</p>
        <button data-testid={`save-${testid}`} onClick={save} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-refuge-ruby text-white text-sm"><Save size={14}/>Enregistrer</button>
      </div>
      <textarea data-testid={`editor-${testid}`} value={txt} onChange={e=>setTxt(e.target.value)} className="w-full h-[420px] font-mono text-xs p-4 border border-refuge-border rounded-lg bg-white" />
      {msg && <p className="mt-2 text-sm text-refuge-ruby">{msg}</p>}
    </div>
  );
}

function ReviewsPanel({ site }) {
  const [data, setData] = useState({ reviews: [], stats: [] });
  const [msg, setMsg] = useState("");
  const load = () => api.getReviews(site).then(setData);
  useEffect(() => { load(); }, [site]);
  const add = async () => {
    await api.addReview({ site, text: "Nouveau témoignage…", name: "Nom", place: "Lieu · Mois Année" });
    load();
  };
  const save = async (r) => { await api.editReview(r._id, { site, text: r.text, name: r.name, place: r.place }); setMsg("✓ Enregistré"); setTimeout(()=>setMsg(""),1500); };
  const del = async (id) => { if (!window.confirm("Supprimer cet avis ?")) return; await api.delReview(id); load(); };
  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="font-heading text-2xl font-light text-refuge-text">Avis voyageurs ({data.reviews.length})</h2>
        <button data-testid="add-review" onClick={add} className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-refuge-ruby text-white text-sm"><Plus size={14}/>Ajouter</button>
      </div>
      {msg && <p className="mb-3 text-sm text-refuge-ruby">{msg}</p>}
      <div className="space-y-4">
        {data.reviews.map((r, i) => (
          <div key={r._id} data-testid={`review-row-${i}`} className="bg-white p-5 border border-refuge-border rounded-xl">
            <textarea value={r.text} onChange={e=>setData(d=>({...d, reviews: d.reviews.map(x=>x._id===r._id?{...x,text:e.target.value}:x)}))} className="w-full mb-3 p-3 border border-refuge-border rounded font-body text-sm" rows="3" />
            <div className="grid grid-cols-2 gap-3 mb-3">
              <input value={r.name} onChange={e=>setData(d=>({...d, reviews: d.reviews.map(x=>x._id===r._id?{...x,name:e.target.value}:x)}))} placeholder="Prénom" className="p-2 border border-refuge-border rounded font-body text-sm" />
              <input value={r.place} onChange={e=>setData(d=>({...d, reviews: d.reviews.map(x=>x._id===r._id?{...x,place:e.target.value}:x)}))} placeholder="Lieu · Date" className="p-2 border border-refuge-border rounded font-body text-sm" />
            </div>
            <div className="flex gap-2">
              <button onClick={()=>save(r)} className="px-3 py-1.5 rounded-full bg-refuge-text text-white text-xs">Enregistrer</button>
              <button onClick={()=>del(r._id)} className="px-3 py-1.5 rounded-full border border-refuge-ruby text-refuge-ruby text-xs inline-flex items-center gap-1"><Trash2 size={12}/>Supprimer</button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function HistoryPanel({ site }) {
  const [items, setItems] = useState([]);
  const [msg, setMsg] = useState("");
  const load = () => api.getHistory(site).then(setItems);
  useEffect(() => { load(); }, [site]);
  const restore = async (h) => {
    if (!window.confirm(`Restaurer cette version du ${new Date(h.changedAt).toLocaleString()} ?`)) return;
    await api.restore(h._id); setMsg("✓ Restauré"); setTimeout(()=>setMsg(""),1500); load();
  };
  return (
    <div>
      <h2 className="font-heading text-2xl font-light text-refuge-text mb-6">Historique ({items.length})</h2>
      {msg && <p className="mb-3 text-sm text-refuge-ruby">{msg}</p>}
      <div className="space-y-2">
        {items.map(h => (
          <div key={h._id} className="bg-white p-4 border border-refuge-border rounded-lg flex justify-between items-center">
            <div>
              <p className="font-body text-sm text-refuge-text">
                <span className="font-mono text-xs bg-refuge-bg px-2 py-0.5 rounded mr-2">{h.kind}</span>
                {h.path}
              </p>
              <p className="font-body text-xs text-refuge-soft mt-1">
                {new Date(h.changedAt).toLocaleString("fr-FR")} · {h.changedBy}
              </p>
            </div>
            <button onClick={()=>restore(h)} className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-refuge-ruby text-refuge-ruby text-xs">
              <RotateCcw size={12}/>Restaurer
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============================================================
// ESPACE 1 · SITE WEB — dashboard tabbed (routes /admin/site)
// ============================================================
function AdminSite() {
  const location = useLocation();
  const [tab, setTab] = useState("guide");
  const [site, setSite] = useState("refuge");
  const [sites, setSites] = useState([]);
  const [content, setContent] = useState({});
  const [pricing, setPricing] = useState({});
  const [contact, setContact] = useState({});

  useEffect(() => {
    api.getSites().then(setSites);
    api.getContent(site).then(setContent);
    api.getPricing(site).then(setPricing);
    api.getContact(site).then(setContact);
  }, [site]);

  // Reset au tab par défaut si on revient sur /admin/site
  useEffect(() => { setTab("accueil"); }, [location.pathname]);

  const doExport = async () => {
    const d = await api.exportAll(site);
    const blob = new Blob([JSON.stringify(d, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `curieux-hub-${site}-${new Date().toISOString().slice(0,10)}.json`; a.click();
    URL.revokeObjectURL(url);
  };

  const tabs = [
    { id: "accueil", label: "Accueil" },
    { id: "guide", label: "Guide" },
    { id: "textes", label: "Textes" },
    { id: "tarifs", label: "Tarifs" },
    { id: "avis", label: "Avis" },
    { id: "contact", label: "Contact" },
    { id: "medias", label: "Médias" },
    { id: "historique", label: "Historique" },
  ];

  return (
    <div className="min-h-screen bg-refuge-bg" data-testid="admin-site">
      <AdminHeader withBack />
      <div className="bg-white border-b border-refuge-border px-4 md:px-6 py-3 flex flex-wrap items-center gap-3 md:gap-4">
        <p className="font-body text-[11px] tracking-[0.22em] uppercase text-refuge-ruby">Site web</p>
        <select data-testid="site-selector" value={site} onChange={e=>setSite(e.target.value)} className="border border-refuge-border rounded-lg px-3 py-1 font-body text-sm">
          {sites.map(s => <option key={s.slug} value={s.slug}>{s.name} {s.status==="draft" && "(brouillon)"}</option>)}
        </select>
        <div className="flex-1" />
        <button onClick={doExport} data-testid="export-btn" className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-refuge-text text-refuge-text text-xs"><Download size={12}/>Export JSON</button>
      </div>

      <nav className="bg-white border-b border-refuge-border px-4 md:px-6">
        <div className="flex gap-1 overflow-x-auto">
          {tabs.map(t => (
            <button key={t.id} data-testid={`tab-${t.id}`} onClick={()=>setTab(t.id)} className={`px-4 py-3 font-body text-sm border-b-2 transition-colors whitespace-nowrap ${tab===t.id ? "border-refuge-ruby text-refuge-ruby" : "border-transparent text-refuge-soft hover:text-refuge-text"}`}>{t.label}</button>
          ))}
        </div>
      </nav>

      <main className="max-w-6xl mx-auto p-4 md:p-8 lg:p-10">
        {tab === "accueil" && <HomePanel site={site} />}
        {tab === "guide" && <GuideEditor site={site} />}
        {tab === "textes" && <JsonEditor label="Textes du site" value={content} onSave={(d)=>api.putContent(site, d).then(()=>setContent(d))} testid="content" />}
        {tab === "tarifs" && <JsonEditor label="Tarifs" value={pricing} onSave={(d)=>api.putPricing(site, d).then(()=>setPricing(d))} testid="pricing" />}
        {tab === "contact" && <JsonEditor label="Contact (WhatsApp, Lodgify, guide)" value={contact} onSave={(d)=>api.putContact(site, d).then(()=>setContact(d))} testid="contact" />}
        {tab === "avis" && <ReviewsPanel site={site} />}
        {tab === "medias" && <MediaPanel site={site} />}
        {tab === "historique" && <HistoryPanel site={site} />}
      </main>
    </div>
  );
}

// ============================================================
// ESPACE 2 · APPLICATION DE GESTION — placeholder Refuge Guests
// ============================================================
function AdminGestion() {
  return (
    <div className="min-h-screen bg-refuge-bg" data-testid="admin-gestion">
      <AdminHeader withBack />
      <main className="max-w-3xl mx-auto px-5 sm:px-8 py-12 md:py-20 text-center">
        <div
          className="w-14 h-14 rounded-full flex items-center justify-center mx-auto mb-6"
          style={{ background: "rgba(232, 184, 189, 0.18)", color: "var(--cr-ruby)" }}
        >
          <Sparkles size={22} strokeWidth={1.3} />
        </div>
        <p className="font-body text-[10px] tracking-[0.32em] uppercase text-refuge-ruby mb-3">
          Espace 2
        </p>
        <h1 className="font-heading text-3xl font-light text-refuge-text mb-4 leading-tight">
          Application de <span className="italic">gestion</span>
        </h1>
        <p className="font-body text-sm text-refuge-soft leading-relaxed max-w-lg mx-auto mb-8">
          Cette section accueillera l'application interne <em>Refuge Guests</em> — calendrier,
          réservations, fiches voyageurs, ménage, marché, checklists. Le code de l'application
          existante sera intégré ici sans perte de fonctionnalités ni de données.
        </p>
        <div
          className="max-w-lg mx-auto rounded-xl bg-white border border-refuge-border p-5 md:p-6 text-left"
          style={{ boxShadow: "0 8px 24px -18px rgba(46, 31, 24, 0.25)" }}
        >
          <p className="font-body text-[10px] tracking-[0.22em] uppercase text-refuge-ruby mb-3">
            Pour finaliser l'intégration
          </p>
          <ol className="space-y-2.5 text-sm text-refuge-text font-body">
            <li className="flex gap-3">
              <span className="font-mono text-refuge-ruby">1.</span>
              <span>Ouvrez le projet <em>Refuge Guests</em> dans Emergent et utilisez « Save to GitHub » pour l'envoyer sur votre GitHub.</span>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-refuge-ruby">2.</span>
              <span>Partagez-moi l'URL du repository GitHub.</span>
            </li>
            <li className="flex gap-3">
              <span className="font-mono text-refuge-ruby">3.</span>
              <span>Je clonerai le code, fusionnerai les modèles de données, monterai les routes sous <span className="font-mono text-[11px] bg-refuge-bg px-1.5 py-0.5 rounded">/admin/gestion</span> et vérifierai l'authentification partagée.</span>
            </li>
          </ol>
        </div>
        <Link
          to="/admin"
          className="inline-flex items-center gap-2 mt-8 px-5 py-2.5 rounded-full border border-refuge-ruby text-refuge-ruby text-xs tracking-[0.2em] uppercase font-body hover:bg-refuge-ruby hover:text-white transition-all"
        >
          <ArrowLeft size={12} />
          Retour au tableau de bord
        </Link>
      </main>
    </div>
  );
}

// ============================================================
// ESPACE 3 · BONS CADEAUX
// ============================================================
function AdminBonsCadeaux() {
  const [site, setSite] = useState("refuge");
  const [sites, setSites] = useState([]);
  useEffect(() => { api.getSites().then(setSites); }, []);
  return (
    <div className="min-h-screen bg-refuge-bg" data-testid="admin-bons-cadeaux">
      <AdminHeader withBack />
      <div className="bg-white border-b border-refuge-border px-4 md:px-6 py-3 flex flex-wrap items-center gap-3">
        <p className="font-body text-[11px] tracking-[0.22em] uppercase text-refuge-ruby">Bons cadeaux</p>
        {sites.length > 1 && (
          <select value={site} onChange={e=>setSite(e.target.value)} className="border border-refuge-border rounded-lg px-3 py-1 font-body text-sm">
            {sites.map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
          </select>
        )}
      </div>
      <main className="max-w-6xl mx-auto p-4 md:p-8 lg:p-10">
        <GiftCardsPanel site={site} />
      </main>
    </div>
  );
}

// ============================================================
// EXPORT — arbre de routes
// ============================================================
export default function Admin() {
  return (
    <Routes>
      <Route element={<AuthGate />}>
        <Route path="/" element={<AdminHub />} />
        <Route path="site" element={<AdminSite />} />
        <Route path="gestion" element={<GestionLayout />}>
          <Route index element={<Navigate to="today" replace />} />
          <Route path="today" element={<GestionToday />} />
          <Route path="finances" element={<GestionFinances />} />
          <Route path="calendrier" element={<GestionCalendar />} />
          <Route path="parcours" element={<GestionDashboard />} />
          <Route path="voyageurs" element={<GestionGuests />} />
          <Route path="voyageur/:id" element={<GestionGuestDetail />} />
          <Route path="reglages" element={<GestionSettings />} />
          <Route path="liste-preparation/print" element={<PrepMasterListPrint />} />
        </Route>
        <Route path="bons-cadeaux" element={<AdminBonsCadeaux />} />
        <Route path="*" element={<Navigate to="/admin" replace />} />
      </Route>
    </Routes>
  );
}
